import { Eye, Heart } from "lucide-react";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Price } from "@/components/ui/price";
import { PUBLIC_ROUTES } from "@/constants/routes";
import type { Product, ProductStatus } from "@/features/marketplace/types/product";

const STATUS_VARIANT: Record<ProductStatus, "success" | "neutral" | "warning"> = {
  active: "success",
  sold: "neutral",
  draft: "warning",
  archived: "neutral",
};

const STATUS_LABEL: Record<ProductStatus, string> = {
  active: "Active",
  sold: "Sold",
  draft: "Draft",
  archived: "Archived",
};

interface MyListingCardProps {
  product: Product;
  onMarkSold: (id: string) => void;
  isMarkingSold: boolean;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

/**
 * docs/architecture/claude/ui-architecture.md's `MyListingCard`: "seller's
 * own listing — stats row (views/favorites/chats), action buttons
 * (Edit/Mark as Sold/Promote)." `chats` and `Promote` are omitted, not
 * stubbed: Chat isn't built yet (there's nothing real to count), and
 * Promote has no backend concept behind it at all — a disabled button that
 * can never do anything is worse than not rendering one (the same call
 * `ProductCard`'s `onFavoriteToggle` already makes). `Edit` links to
 * `/listings/:id/edit`, which `docs/ui/routes.md` itself flags as "not yet
 * wireframed" — it still renders as a real link so the gap is visible, not
 * hidden.
 */
export function MyListingCard({
  product,
  onMarkSold,
  isMarkingSold,
  onDelete,
  isDeleting,
}: MyListingCardProps) {
  const canMarkSold = product.status === "active";

  return (
    <div className="flex gap-4 rounded-lg border border-border bg-card p-4">
      <div className="h-20 w-20 shrink-0 overflow-hidden rounded-md bg-muted">
        {product.images[0] && (
          <img src={product.images[0]} alt="" className="h-full w-full object-cover" />
        )}
      </div>

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <Link
            to={PUBLIC_ROUTES.productDetails.replace(":slug", product.slug)}
            className="truncate text-body font-medium text-foreground hover:underline"
          >
            {product.title}
          </Link>
          <Badge variant={STATUS_VARIANT[product.status]}>{STATUS_LABEL[product.status]}</Badge>
        </div>
        <Price value={product.price} size="body-lg" />
        <div className="flex items-center gap-4 text-body-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            {product.views}
          </span>
          <span className="flex items-center gap-1">
            <Heart className="h-3.5 w-3.5" aria-hidden="true" />
            {product.favoritesCount}
          </span>
        </div>

        <div className="flex flex-wrap gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" asChild>
            <Link to={PUBLIC_ROUTES.productDetails.replace(":slug", product.slug)}>View</Link>
          </Button>
          {canMarkSold && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              isLoading={isMarkingSold}
              onClick={() => onMarkSold(product.id)}
            >
              Mark as Sold
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            isLoading={isDeleting}
            onClick={() => onDelete(product.id)}
          >
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
